import { z } from "zod";

import { ImpulseFormShape, ImpulseFormUnit } from "~/tools/impulse-form";
import { isStringDirty } from "~/tools/is-string-dirty";

import type { DashboardSpec, DashboardWidgetSpecInitConfig } from "../../common/dashboard-spec";
import type { DashboardReadFullSchema } from "../../queries/dashboards";

export interface InitSetupDashboardFormStateParams {
	name: string;
	description: string;
	starred: boolean;
	publiclyAccessible: boolean;
}

export function initSetupDashboardFormState({
	name,
	description,
	starred,
	publiclyAccessible,
}: InitSetupDashboardFormStateParams) {
	return ImpulseFormShape({
		dashboard: ImpulseFormShape({
			name: ImpulseFormUnit(name, {
				schema: z.string().trim().min(1, "Dashboard name is required"),
				isInputDirty: isStringDirty,
			}),

			description: ImpulseFormUnit(description, {
				isInputDirty: isStringDirty,
				schema: z.string().trim(),
			}),
		}),
		options: ImpulseFormShape({
			starred: ImpulseFormUnit(starred),
			publiclyAccessible: ImpulseFormUnit(publiclyAccessible),
		}),
	});
}

export type SetupDashboardFormState = ReturnType<typeof initSetupDashboardFormState>;

export type SetupDashboardDrawerState =
	| {
			type: "create";
			form: SetupDashboardFormState;
			/**
			 * Initial widgets to be added to the dashboard upon creation.
			 *
			 * @default []
			 */
			widgets?: ReadonlyArray<DashboardWidgetSpecInitConfig>;
			/**
			 * Callback function to be executed on successful dashboard creation.
			 * It navigates to the created dashboard by default.
			 */
			onSuccess?: (dashboard: DashboardReadFullSchema) => void;
	  }
	| {
			type: "clone";
			id: number;
			/**
			 * The dashboard spec to override the original one.
			 */
			spec?: DashboardSpec;
			form: SetupDashboardFormState;
	  }
	| {
			type: "edit";
			id: number;
			form: SetupDashboardFormState;
			canSave: boolean;
	  };
